import React, { useEffect, useState } from 'react'
import { Button, Divider, Form, message, Modal, Row, Typography } from 'antd'
import { useForm } from 'antd/es/form/Form'
import Stepper from 'components/Stepper/Stepper'
import Storage from 'util/store/Storage'
import useGenericInfo from 'hooks/useGenericInfo'
import useRecursos from '../hooks/useConfiguracionRecursos'
import { ArchivoRecurso, ESTADOS_RECURSO } from '../interfaces/ConfiguracionRecursos.interfaces'
import { FIELDS_EDICION, FIELDS_NAMES_RECURSO, FIELDS_STEP_2 } from '../interfaces/FieldNamesRecurso'
import useUbicacionEstaciones from '../hooks/useUbicacionEstaciones'
import PasoUbicacionEstaciones from './PasoUbicacionEstaciones'
import PasoDatosRecurso from './PasoDatosRecurso'
import { getSubtituloModal, TITULOS_MODAL_RECURSO } from '../helpers/ModalRecursos.helper'
import './index.css'

const contenidoScroll: React.CSSProperties = {
  maxHeight: 'calc(100vh - 320px)',
  overflowY: 'auto',
  paddingLeft: 8,
  paddingRight: 8,
  marginLeft: -8,
  marginRight: -8,
}

const ModalRecurso = () => {
  const { modalOpen, modo, recursoSelected, closeModal, crearRecurso, editarRecurso, fetchDetalle } = useRecursos()

  const soloLectura = modo === 'ver'
  const isWizard = modo === 'crear'
  const isEdicion = modo === 'editar'

  const { empresasHijos, organizationByUser } = useGenericInfo({ organizations: true })
  const { negocio } = Storage.getState()

  const [form] = useForm()
  const [currentStep, setCurrentStep] = useState<0 | 1>(0)
  const [submitting, setSubmitting] = useState(false)
  const [empresaRecurso, setEmpresaRecurso] = useState<number | undefined>(undefined)

  const {
    regionalesOptions,
    loadingRegionales,
    empresasOptions,
    loadingEmpresas,
    autoseleccionadas,
    onChangeOrganizacion,
    onChangeRegionales,
    fijarUbicacion,
    reset: resetUbicacion,
  } = useUbicacionEstaciones({
    form,
    activo: modalOpen,
    modoCreacion: isWizard,
    negocio,
    empresasHijos,
    organizacionesOptions: organizationByUser,
  })

  const empresasSeleccionadas: number[] =
    Form.useWatch(FIELDS_NAMES_RECURSO.EMPRESAS, { form, preserve: true }) ?? []

  const archivosSeleccionados: ArchivoRecurso[] =
    Form.useWatch(FIELDS_NAMES_RECURSO.ARCHIVOS, { form, preserve: true }) ?? []

  const step1Valido = empresasSeleccionadas.length > 0

  const textoBotonCrear = `Crear ${archivosSeleccionados.length > 1 ? `${archivosSeleccionados.length} recursos` : 'recurso'}${
    empresasSeleccionadas.length > 1 ? ` (${empresasSeleccionadas.length} estaciones)` : ''
  }`

  const tituloModal = TITULOS_MODAL_RECURSO[modo!]
  const subtituloModal = getSubtituloModal(modo!, currentStep === 0 ? 1 : 2)

  useEffect(() => {
    if (!modalOpen) return

    form.resetFields()
    setCurrentStep(0)

    if (!recursoSelected) {
      resetUbicacion()
      setEmpresaRecurso(undefined)
      form.setFieldValue(FIELDS_NAMES_RECURSO.ACTIVO, true)
      return
    }

    form.setFieldsValue({
      [FIELDS_NAMES_RECURSO.TIPO]: recursoSelected.tipo_id,
      [FIELDS_NAMES_RECURSO.NOMBRE]: recursoSelected.nombre,
      [FIELDS_NAMES_RECURSO.DESCRIPCION]: recursoSelected.descripcion ?? '',
      [FIELDS_NAMES_RECURSO.ORDEN]: recursoSelected.orden ?? undefined,
      [FIELDS_NAMES_RECURSO.URL]: recursoSelected.url,
      [FIELDS_NAMES_RECURSO.ACTIVO]: recursoSelected.estado_id === ESTADOS_RECURSO.ACTIVO,
    })
    setEmpresaRecurso(recursoSelected.empresa_id)
    if (recursoSelected.empresa_id) {
      fijarUbicacion({
        label: recursoSelected.empresa_nombre ?? `Estación ${recursoSelected.empresa_id}`,
        value: recursoSelected.empresa_id,
      })
    }

    let cancelado = false
    fetchDetalle(recursoSelected.id).then((detalle) => {
      if (cancelado || !detalle) return

      form.setFieldsValue({
        [FIELDS_NAMES_RECURSO.TIPO]: detalle.tipo_id,
        [FIELDS_NAMES_RECURSO.NOMBRE]: detalle.nombre,
        [FIELDS_NAMES_RECURSO.DESCRIPCION]: detalle.descripcion ?? '',
        [FIELDS_NAMES_RECURSO.ORDEN]: detalle.orden ?? undefined,
        [FIELDS_NAMES_RECURSO.URL]: detalle.url ?? undefined,
        [FIELDS_NAMES_RECURSO.ACTIVO]: detalle.estado_id === ESTADOS_RECURSO.ACTIVO,
      })
      setEmpresaRecurso(detalle.empresa_id)
      if (detalle.empresa_id) {
        fijarUbicacion({
          label: detalle.empresa_nombre ?? `Estación ${detalle.empresa_id}`,
          value: detalle.empresa_id,
        })
      }
    })

    return () => {
      cancelado = true
    }
  }, [recursoSelected, modalOpen])

  const handleClose = () => {
    form.resetFields()
    resetUbicacion()
    setCurrentStep(0)
    setEmpresaRecurso(undefined)
    closeModal()
  }

  const next = () => {
    if (!step1Valido) return
    setCurrentStep(1)
  }

  const prev = () => setCurrentStep(0)

  const handleSubmit = async () => {
    if (isWizard && empresasSeleccionadas.length === 0) {
      message.warning('Selecciona al menos una estación antes de crear el recurso')
      setCurrentStep(0)
      return
    }

    try {
      const values = await form.validateFields(isWizard ? FIELDS_STEP_2 : FIELDS_EDICION)
      const activo = form.getFieldValue(FIELDS_NAMES_RECURSO.ACTIVO)
      const archivos: ArchivoRecurso[] = values.archivos ?? []
      const estado_id = activo ? ESTADOS_RECURSO.ACTIVO : ESTADOS_RECURSO.INACTIVO
      setSubmitting(true)

      const resultado =
        isEdicion && recursoSelected
          ? await editarRecurso(
              {
                id: recursoSelected.id,
                tipo_id: values.tipo_id,
                nombre: values.nombre,
                descripcion: form.getFieldValue(FIELDS_NAMES_RECURSO.DESCRIPCION) ?? '',
                orden: Number(values.orden) || undefined,
                url: form.getFieldValue(FIELDS_NAMES_RECURSO.URL) ?? '',
                estado_id,
                archivo: archivos[0]?.archivo,
              },
              archivos.slice(1),
              empresaRecurso
            )
          : await crearRecurso(
              {
                tipo_id: values.tipo_id,
                estado_id,
                archivos,
              },
              empresasSeleccionadas
            )

      if (resultado.success) handleClose()
    } catch (error: any) {
      if (error?.errorFields) return
      message.error(error?.response?.data?.message ?? error?.message ?? 'No se pudo guardar el recurso')
    } finally {
      setSubmitting(false)
    }
  }

  const pasoUbicacion = (
    <PasoUbicacionEstaciones
      form={form}
      organizacionesOptions={organizationByUser}
      regionalesOptions={regionalesOptions}
      loadingRegionales={loadingRegionales}
      empresasOptions={empresasOptions}
      loadingEmpresas={loadingEmpresas}
      autoseleccionadas={autoseleccionadas}
      onChangeOrganizacion={onChangeOrganizacion}
      onChangeRegionales={onChangeRegionales}
      soloLectura={!isWizard}
    />
  )

  const pasoDatos = (
    <PasoDatosRecurso
      form={form}
      readOnly={soloLectura}
      mostrarEstado={!isWizard}
      permitirVariosArchivos={!soloLectura}
      esEdicion={isEdicion}
    />
  )

  return (
    <Modal open={modalOpen} onCancel={handleClose} footer={null} centered width={600} destroyOnClose>
      <Row justify="center" align="middle" style={{ marginBottom: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {tituloModal}
        </Typography.Title>
      </Row>
      <Row justify="center" align="middle" style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {subtituloModal}
        </Typography.Text>
      </Row>

      {isWizard && (
        <Stepper
          type="outline"
          controlled
          current={currentStep}
          onChange={(e: any) => step1Valido && setCurrentStep(e)}
          steps={[
            { label: 'Estaciones', key: 'ubicacion' },
            { label: 'Datos del recurso', key: 'datos' },
          ]}
        />
      )}

      <div style={contenidoScroll}>
        <Form form={form} layout="vertical" disabled={soloLectura} style={{ marginTop: 16 }}>
          {isWizard ? (
            currentStep === 0 ? pasoUbicacion : pasoDatos
          ) : (
            <>
              {pasoUbicacion}
              <Divider style={{ margin: '16px 0' }} />
              {pasoDatos}
            </>
          )}
        </Form>
      </div>

      {!soloLectura && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {isWizard && currentStep === 1 && (
            <Button block onClick={prev}>
              Atrás
            </Button>
          )}

          {isWizard && currentStep === 0 ? (
            <Button
              type="primary"
              block
              disabled={!step1Valido}
              onClick={next}
              style={{ borderRadius: 12, fontWeight: 'bold' }}
            >
              Siguiente
            </Button>
          ) : (
            <Button
              type="primary"
              block
              loading={submitting}
              onClick={handleSubmit}
              style={{ borderRadius: 12, fontWeight: 'bold' }}
            >
              ✓ {isEdicion ? 'Guardar cambios' : textoBotonCrear}
            </Button>
          )}
        </div>
      )}
    </Modal>
  )
}

export default ModalRecurso
